import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from '../RootStore';

export class NotificationAPIStore {
    rootStore: RootStore;
    permission: NotificationPermission = 'default';
    isSupported = 'Notification' in window;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;

        if (this.isSupported) {
            this.permission = Notification.permission;
        }
    }

    async requestPermission() {
        if (!this.isSupported) {
            throw new Error('Notifications not supported');
        }

        const permission = await Notification.requestPermission();
        runInAction(() => {
            this.permission = permission;
        });

        return permission;
    }

    async show(
        title: string,
        options?: NotificationOptions
    ): Promise<Notification | null> {
        if (!this.isSupported) {
            console.warn('Notifications not supported');
            return null;
        }

        if (this.permission !== 'granted') {
            const permission = await this.requestPermission();
            if (permission !== 'granted') {
                return null;
            }
        }

        return new Notification(title, {
            icon: '/icon-192x192.png',
            badge: '/icon-72x72.png',
            ...options,
        });
    }

    async showWithActions(
        title: string,
        body: string,
        actions: { action: string; title: string; icon?: string }[]
    ) {
        if (!this.isSupported || !('serviceWorker' in navigator)) {
            console.warn('Service worker notifications not supported');
            return;
        }

        const registration = await navigator.serviceWorker.ready;

        return registration.showNotification(title, {
            body,
            actions,
            icon: '/icon-192x192.png',
            badge: '/icon-72x72.png',
        } as any);
    }
}